<template>
  <div class="side-nav-container">
    <!-- Slide out Nav -->
    <div class="off-canvas-nav-container">
      <b-nav
        class="off-canvas-nav"
        v-bind:class="{ isAnimated: sidebarShowing }"
        @mouseover="toggleOnCanvasNav"
        @mouseout="toggleOffCanvasNav"
        vertical
      >
        <!-- Generate Logo -->
        <!-- <b-nav-item style="">
            <img class="logo-header" :style="{ backgroundImage: 'url(' + require('@/assets/images/pitaya_wordmark_white.png') + ')' }"></img>
        </b-nav-item> -->

        <!-- Generate the Search Input -->
        <b-nav-item>
          <input
            class="search-input"
            :placeholder="$t('sidenav.search').toUpperCase()"
            v-model="searchValue"
            @focus="searchHasFocus"
            @blur="searchBlurred"
          />
        </b-nav-item>

        <!-- Generate the Standard Style Menu Links -->
        <localized-link
          v-for="link in filteredPageLinks"
          v-bind:key="link.name"
          :to="link.to"
          class="nav_menu_link"
          active-class="navItemActive"
          @click="selectLink(link.name)"
        >
          <div @mouseover="mouseOver(link.name)" @mouseout="mouseOutOfHover">
            <b-nav-item
              style="pointer-events: none"
              :class="{ navItemHover: link.name === hoveredLink }"
            >
              <span class="nav-menu-link">{{
                $t(link.name).toUpperCase()
              }}</span>
            </b-nav-item>
          </div>
        </localized-link>
      </b-nav>
    </div>

    <!-- Icon Buttons -->
    <b-nav
      class="sidenavbar"
      vertical
      @mouseover="toggleOnCanvasNav"
      @mouseout="toggleOffCanvasNav"
      @click="toggleSidebar"
    >
      <!-- Pitaya logo & name space -->
      <!-- <b-nav-item class="">
        <div class="logo-block">
          <div class="header-logo" :style="{ backgroundImage: 'url(' + require('@/assets/images/logo_icon.png') + ')' }"></div>
        </div>
      </b-nav-item> -->

      <!-- Search Menu Icon -->
      <b-nav-item>
        <i class="fi-magnifying-glass nav-icon" />
      </b-nav-item>

      <!-- Generate Remainder of Icons -->
      <localized-link
        v-for="link in filteredPageLinks"
        v-bind:key="link.name + 'icon'"
        :to="link.to"
        active-class="navItemActive"
        @click="selectLink(link.name)"
        :class="{ navIconHover: link.name == hoveredLink }"
      >
        <div
          @mouseover="mouseOver(link.name)"
          @mouseout="mouseOutOfHover"
          class="nav-item-icon"
        >
          <b-nav-item data-nav-id="1">
            <i
              :class="'fi-' + link.icon + ' nav-icon'"
              style="pointer-events: none"
            />
          </b-nav-item>
        </div>
      </localized-link>

      <!-- Generate Search Results List -->
      <div
        class="search-results-list"
        v-if="
          (!!filteredContacts && filteredContacts.length > 0) ||
          (!!filteredBookings && filteredBookings.length > 0)
        "
      >
        <div class="search-results-arrow-up"></div>
        <div class="search-results-list-box">
          <ul>
            <li class="list-divider" v-if="filteredBookings.length > 0">
              <b>{{ $t("sidenav.booking_results") }}</b>
            </li>
            <li
              v-for="booking in filteredBookings"
              @click="handleBookingClicked(booking)"
              :key="`booking-${booking.id}`"
            >
              <div class="search-preview-photo">
                <picture-placeholder
                  backgroundColor="#FCFCFC"
                  color="#3A3B3D"
                  fontSize="20px"
                  :textIn="booking.name"
                />
              </div>
              <div class="search-results-card-title">
                {{ booking.name }}
                <span class="booking-date-span"
                  >({{ formatDate(booking.starts_at) }} -
                  {{ formatDate(booking.ends_at) }})</span
                >
              </div>
            </li>

            <li class="list-divider" v-if="filteredContacts.length > 0">
              <b>{{ $t("sidenav.contact_results") }}</b>
            </li>
            <li
              v-for="contact in filteredContacts"
              @click="handleContactClicked(contact)"
              :key="`contact-${contact.id}`"
            >
              <div class="search-preview-photo">
                <picture-placeholder
                  :pictureUrl="contact.photo_sm_uri || contact.photo_uri"
                  backgroundColor="#FCFCFC"
                  color="#3A3B3D"
                  fontSize="20px"
                  :textIn="
                    contact.first_name.toUpperCase() +
                    ' ' +
                    contact.last_name.toUpperCase()
                  "
                />
              </div>
              <div class="search-results-card-title">
                {{ contact.first_name }} {{ contact.last_name }}
                {{ contact.email ? `(${contact.email})` : null }}
              </div>
            </li>
          </ul>
        </div>
      </div>
    </b-nav>

    <!-- Import the ContactModal in case we need to edit -->
    <contact-modal ref="contactModal">
      <template slot="submit">
        <b-button @click="handleUpdateContact">Update</b-button>
      </template>
    </contact-modal>
    <booking-modal ref="bookingModal">
      <template slot="submit" />
    </booking-modal>
  </div>
</template>

<script>
/* eslint-disable */
import LocalizedLink from "./LocalizedLink";
import ContactModal from "./modals/ContactModal";
import BookingModal from "./modals/BookingModal";
import PicturePlaceholder from "@/components/widgets/PicturePlaceholder";
import { formatBookingSearchPeriod } from "@/utils/date-helper";
import cloneDeep from "lodash.clonedeep";

import { mapGetters, mapState, mapMutations } from "vuex";
export default {
  data() {
    return {
      contacts: [],
      searchValue: "",
      selectedLink: "sidenav.dashboard",
      hoveredLink: "",
      isSearching: false,
      mouseOut: false,
      standardPageLinks: [
        {
          name: "sidenav.dashboard",
          to: "/dashboard",
          icon: "graph-bar",
        },
        {
          name: "sidenav.reservations",
          to: "/reservations",
          icon: "calendar",
        },
        {
          name: "sidenav.contacts",
          to: "/contacts",
          icon: "torsos",
        },
        {
          name: "sidenav.pos",
          to: "/pos",
          icon: "monitor",
          requiresPOS: true,
        },
        {
          name: "sidenav.tabs",
          to: "/tabs",
          icon: "credit-card",
        },
        {
          name: "sidenav.analytics",
          to: "/analytics",
          icon: "graph-pie",
        },
        {
          name: "sidenav.settings",
          to: "/settings",
          icon: "widget",
        },
      ],
    };
  },
  methods: {
    ...mapMutations({
      toggleSidebar: "toggleSidebar",
      showSidebar: "showSidebar",
      hideSidebar: "hideSidebar",
    }),
    searchHasFocus(_hasFocus) {
      this.isSearching = true;
    },
    searchBlurred() {
      this.isSearching = false;
      if (this.mouseOut == true) {
        this.closeOffCanvasNav();
      }
    },
    closeOffCanvasNav() {
      this.hideSidebar();
      this.searchValue = "";
    },
    selectLink(_name) {
      this.selectedLink = _name;
      this.closeOffCanvasNav();
      this.mouseOutOfHover();
    },
    toggleOffCanvasNav() {
      if (!this.isSearching) {
        this.closeOffCanvasNav();
      }
      this.mouseOut = true;
    },
    toggleOnCanvasNav() {
      this.showSidebar();
      this.mouseOut = false;
    },
    mouseOver(_link) {
      this.hoveredLink = _link;
    },
    mouseOutOfHover() {
      this.hoveredLink = "";
    },
    toUpper(_str) {
      var str = String.valueOf(_str);
      return _str.toUpperCase();
    },
    handleContactClicked(contact) {
      this.$refs.contactModal.show(contact);
    },
    handleBookingClicked(booking) {
      const firstReservation = booking.reservations[0];
      if (!firstReservation) {
        this.errorNotification("This booking has no rooms reserved.");
      } else {
        this.$refs.bookingModal.show(firstReservation);
      }
    },
    handleUpdateContact() {
      this.$refs.contactModal.updateContact();
      this.$refs.contactModal.hide();
    },
    formatDate(date) {
      return formatBookingSearchPeriod(date);
    },
    handleLogout() {
      console.log("Logout button clicked in SideNavBar");
      this.$store
        .dispatch("logout")
        .then(() => {
          console.log("Logout action dispatched successfully from SideNavBar.");
          // The redirection should be handled by the authModule's logout action now
        })
        .catch((error) => {
          console.error(
            "Error dispatching logout action from SideNavBar:",
            error
          );
        });
    },
  },
  components: {
    LocalizedLink,
    ContactModal,
    BookingModal,
    PicturePlaceholder,
  },
  computed: {
    ...mapGetters(["getContactsCopy", "getBookings"]),
    ...mapGetters("subscription", ["hasPOSAccess"]),
    ...mapState({
      sidebarShowing: (state) => state.ui.sidebarShowing,
      logoutRequest: (state) => state.auth.isRequesting,
    }),
    filteredPageLinks() {
      return this.standardPageLinks.filter((link) => {
        if (link.requiresPOS) {
          return this.hasPOSAccess;
        }
        return true;
      });
    },
    filteredContacts() {
      if (this.searchValue) {
        const searchTerms = this.searchValue.split(" ");
        if (searchTerms.length > 1) {
          let matches = cloneDeep(this.getContactsCopy);
          searchTerms.forEach((searchTerm) => {
            matches = matches.filter(
              (match) =>
                match.first_name
                  .toLowerCase()
                  .includes(searchTerm.toLowerCase()) ||
                match.last_name.toLowerCase().includes(searchTerm.toLowerCase())
            );
          });
          return matches;
        } else {
          return this.getContactsCopy.filter(
            (item) =>
              item.first_name
                .toLowerCase()
                .includes(this.searchValue.toLowerCase()) ||
              item.last_name
                .toLowerCase()
                .includes(this.searchValue.toLowerCase())
          );
        }
      }
    },
    filteredBookings() {
      if (this.searchValue) {
        return this.getBookings.filter((booking) =>
          booking.name.toLowerCase().includes(this.searchValue.toLowerCase())
        );
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/style/_variables.scss";
a:hover {
  text-decoration: none;
}
.booking-date-span {
  font-size: small;
}
.search-input {
  width: 184px;
  margin-left: 8px;
  border: solid 1px #0000000c;
  border-radius: 100px;
  background: #f7f7f7;
  color: $main_theme_fg_color;
  text-align: center;
  font-size: 12pt;
}
.search-input:focus {
  outline: none;
}
::placeholder {
  color: #ccc;
}
.search-menu-row {
  background: $grey;
}
.search-results-list-box {
  position: relative;
  max-height: 500px;
  overflow-y: scroll;
}
.search-results-list {
  position: absolute;
  width: 700px;
  max-height: 500px;
  left: 54px;
  top: 60px;
  background: white;
  color: $text_dark;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border: 1px solid $border_color;
  border-radius: 8px;
  overflow: hidden;
  z-index: 1000;

  li.list-divider {
    height: 35px;
    text-decoration: none;
    color: #666;
    background: #f8f9fa;
    padding-left: 20px;
    &:hover {
      background: #f8f9fa;
      cursor: default;
    }
  }
}
.search-results-list ul {
  padding: 0px 0px;
}
.search-results-list li {
  position: relative;
  list-style: none;
  text-align: left;
  height: 54px;
  padding: 3px 10px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
}
.search-results-list li:hover {
  background-color: $primary_blue_light;
  color: $primary_blue;
}
.search-results-card-title {
  position: absolute;
  top: 50%;
  padding-left: 58px;
  transform: translateY(-50%);
  font-size: 14pt;
}
.search-results-arrow-up {
  position: absolute;
  left: 90px;
  top: -10px;
  width: 0;
  height: 0;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-bottom: 10px solid white;
  filter: drop-shadow(0 -2px 2px rgba(0, 0, 0, 0.1));
}
.off-canvas-nav {
  /* Expanded sidenav */
  position: fixed;
  margin-top: 0px;
  padding-left: 54px;
  transform: translateX(0px);
  width: 254px;
  height: 100%;
  text-align: left;
  transition: all 0.3s ease-in-out;
  background-color: white;
  top: 0px;
  left: -200px;
}
@media only screen and (max-width: 1026px) {
  .off-canvas-nav {
    display: none;
  }
}
.sidenavbar {
  /* Actual side nav when collapsed*/
  position: fixed;
  background-color: white;
  width: 54px;
  height: 100%;
  top: 0px;
  left: 0px;
}
.nav-item {
  height: 54px;
  font-size: 14px;
  width: 100%;
}
.nav-link {
  position: relative;
  top: 50%;
  transform: translateY(-45%);
  padding: 0px;
}
.nav-menu-link {
  color: #777 !important;
  padding-left: 14px;
}

.nav-icon {
  position: absolute;
  left: 50%;
  top: -5px;
  transform: translate(-50%, -50%);
  fill: $text_dark;
  color: $text_dark;
  font-size: 25px;
  width: 25px;
  height: 25px;
}
.navItemActive {
  background: $primary_blue_light !important;
  color: $primary_blue !important;
  svg {
    fill: $primary_blue !important;
    color: $primary_blue !important;
  }
  .nav-link {
    color: $primary_blue !important;
  }
  .nav-menu-link {
    color: $primary_blue !important;
  }
  & .nav-icon {
    fill: $primary_blue;
    color: $primary_blue;
  }
}
.navIconHover {
  background: $primary_blue_light !important;
  color: $primary_blue !important;
  svg {
    fill: $primary_blue !important;
  }
  .nav-link {
    color: $primary_blue !important;
  }
  .nav-menu-link {
    color: $primary_blue !important;
  }
  i {
    color: $primary_blue !important;
  }
}
.navItemHover {
  background: $primary_blue_light;
  color: $primary_blue !important;
  & .nav-icon {
    fill: $primary_blue;
    color: $primary_blue;
  }
}
.navItemHover span {
  color: $primary_blue !important;
  opacity: 0.6;
}
.isAnimated {
  transform: translateX(200px);
  border: none;
}
a:not([href]):not([tabindex]) {
  color: #fff;
}
.side-nav-container {
  position: absolute;
  top: 0px;
  left: 0px;
  height: 100%;
}
.off-canvas-nav-container {
  position: absolute;
  padding-top: 54px;
  padding-left: 0px;
  height: 100%;
  width: 54px;
  top: 54px;
  box-sizing: border-box;
}
.search-preview-photo {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  border-radius: 50%;
  height: 44px;
  width: 44px;
  font-size: 14px !important;
  font-weight: 400;
  overflow: hidden;
}
.nav_menu_link:hover {
  background-color: #bbb;
}
@media screen and (min-width: 320px) and (max-width: 767px) and (orientation: landscape) {
  .nav-item {
    height: $mobile-navbar-width;
    font-size: 19px;
    width: 100%;
  }

  .nav-icon {
    position: absolute;
    left: 50%;
    top: -5px;
    transform: translate(-50%, -50%);
    fill: black;
    color: #333;
    font-size: 25px;
    width: 25px;
    height: 25px;
  }
  .sidenavbar {
    position: fixed;
    background-color: #ebebeb;
    width: $mobile-navbar-width;
    top: 0px;
    left: 0px;
    height: 100%;
  }
  .header-logo {
    background-position: center;
    background-size: contain;
    background-repeat: no-repeat;
    width: 50%;
    height: 100%;
    left: 35%;
    position: relative;
  }
}
</style>
